import { FunctionComponent, JSX } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useI18nLabels } from '@/utils/useI18nLabels';
import useDateLocale from '@/hooks/useDateLocale';
import { Title, DetailsRows } from 'UI';
import ApprovalPanel from '../../../UI/ApprovalPanel/ApprovalPanel';
import ScheduledTransactionCard from './ScheduledTransactionCard';
import { composeRepeatLabel, composeEndsLabel, parseLocalDate } from './composeRepeatLabel';
import styles from './ScheduledTransactionOverview.module.scss';

import {
  Box,
  Card,
  formatMaskedAccount,
  capitalizeFirstLetterOfWords,
  MergedScheduledPayment,
  withStyles,
} from 'vopay';

const cn = withStyles(styles);

type DetailRow = {
  label: string;
  value: string | JSX.Element;
  visible?: boolean;
};

type TransactionOverviewProps = {
  transaction: MergedScheduledPayment;
};

const ScheduledTransactionOverview: FunctionComponent<TransactionOverviewProps> = ({ transaction }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'ScheduledTransactions.Details' });
  const { t: tScheduleDetails } = useTranslation('translations', {
    keyPrefix: 'ScheduledTransactions.Details.ScheduleDetailsSection',
  });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { paymentTypeLabelMapper } = useI18nLabels();
  const dateLocale = useDateLocale();

  const isRecurring = transaction.ScheduleType === 'recurring';

  function formatDate(value: string): string {
    const date = parseLocalDate(value);
    return date ? format(date, 'MMM d, yyyy', { locale: dateLocale }) : '';
  }

  const ACCOUNT_DETAILS_TITLE_BY_TYPE: Partial<
    Record<NonNullable<MergedScheduledPayment['TokenData']['Type']>, string>
  > = {
    bank: t('AccountDetailsSection.TitleByType.bank', { defaultValue: 'Bank Account Details' }),
    credit: t('AccountDetailsSection.TitleByType.credit', { defaultValue: 'Credit Card Details' }),
    debit: t('AccountDetailsSection.TitleByType.debit', { defaultValue: 'Debit Card Details' }),
  };
  const accountDetailsTitle =
    (transaction.TokenData.Type && ACCOUNT_DETAILS_TITLE_BY_TYPE[transaction.TokenData.Type]) ||
    t('AccountDetailsSection.title', { defaultValue: 'Account Details' });

  const scheduleDetails: DetailRow[] = [
    {
      label: t('ScheduleDetailsSection.Rows.ScheduleType.label', { defaultValue: 'Schedule Type' }),
      value: t(`ScheduleDetailsSection.Rows.ScheduleType.Options.${transaction.ScheduleType}`, {
        defaultValue: '',
      }),
      visible: true,
    },
    {
      label: t('ScheduleDetailsSection.Rows.startDate', { defaultValue: 'Start Date' }),
      value: transaction.StartDateTime ? formatDate(transaction.StartDateTime) : '',
      visible: isRecurring && !!transaction.StartDateTime,
    },
    {
      label: t('ScheduleDetailsSection.Rows.paymentDate', { defaultValue: 'Payment Date' }),
      value: transaction.StartDateTime ? formatDate(transaction.StartDateTime) : '',
      visible: !isRecurring && !!transaction.StartDateTime,
    },
    {
      label: t('ScheduleDetailsSection.Rows.repeat', { defaultValue: 'Repeat' }),
      value: composeRepeatLabel(transaction, tScheduleDetails, tGlobal, dateLocale),
      visible: isRecurring && !!transaction.Repeats,
    },
    {
      label: t('ScheduleDetailsSection.Rows.ends', { defaultValue: 'Ends' }),
      value: composeEndsLabel(transaction, tScheduleDetails, dateLocale),
      visible: isRecurring && (!!transaction.ScheduleEndDate || Number(transaction.NumberOfPayments) > 0),
    },
    {
      label: t('ScheduleDetailsSection.Rows.Type.label', { defaultValue: 'Type' }),
      value: paymentTypeLabelMapper(transaction.PaymentType),
      visible: true,
    },
    {
      label: t('ScheduleDetailsSection.Rows.description', { defaultValue: 'Description' }),
      value: transaction.Description || '',
      visible: transaction.Description !== '',
    },
  ];

  const accountHolderDetails: DetailRow[] = [
    {
      label: t('AccountHolderDetailsSection.Rows.name', { defaultValue: 'Name' }),
      value: transaction.TokenData.FullName || '',
      visible: !!transaction.TokenData.FullName,
    },
  ];

  const accountDetails: DetailRow[] = [
    {
      label: t('AccountDetailsSection.Rows.email', { defaultValue: 'Email' }),
      value: transaction.TokenData.EmailAddress || '',
      visible: !!transaction.TokenData.EmailAddress,
    },
    {
      label: t('AccountDetailsSection.Rows.financialInstitutionNumber', {
        defaultValue: 'Financial Institution Number',
      }),
      value: transaction.TokenData.Bank || '',
      visible: !!transaction.TokenData.Bank,
    },
    {
      label: t('AccountDetailsSection.Rows.branchTransitNumber', { defaultValue: 'Branch Transit Number' }),
      value: transaction.TokenData.TransitNumber || '',
      visible: !!transaction.TokenData.TransitNumber,
    },
    {
      label: t('AccountDetailsSection.Rows.accountNumber', { defaultValue: 'Account Number' }),
      value: formatMaskedAccount(transaction.TokenData.MaskedAccount || ''),
      visible: !!transaction.TokenData.MaskedAccount,
    },
    {
      label: t('AccountDetailsSection.Rows.creditCardNumber', { defaultValue: 'Credit Card Number' }),
      value: formatMaskedAccount(transaction.TokenData.CreditCardNumber || ''),
      visible: !!transaction.TokenData.CreditCardNumber,
    },
    {
      label: t('AccountDetailsSection.Rows.creditCardBrand', { defaultValue: 'Credit Card Brand' }),
      value: transaction.TokenData.CreditCardBrand
        ? capitalizeFirstLetterOfWords(transaction.TokenData.CreditCardBrand)
        : '',
      visible: !!transaction.TokenData.CreditCardBrand,
    },
    {
      label: t('AccountDetailsSection.Rows.creditCardExpiry', { defaultValue: 'Expiry Date' }),
      value:
        transaction.TokenData.CreditCardExpiryMonth && transaction.TokenData.CreditCardExpiryYear
          ? `${transaction.TokenData.CreditCardExpiryMonth}/${transaction.TokenData.CreditCardExpiryYear}`
          : '',
      visible: !!transaction.TokenData.CreditCardExpiryMonth && !!transaction.TokenData.CreditCardExpiryYear,
    },
    {
      label: t('AccountDetailsSection.Rows.debitCardNumber', { defaultValue: 'Debit Card Number' }),
      value: formatMaskedAccount(transaction.TokenData.DebitCardNumber || ''),
      visible: !!transaction.TokenData.DebitCardNumber,
    },
    {
      label: t('AccountDetailsSection.Rows.debitCardBrand', { defaultValue: 'Debit Card Brand' }),
      value: transaction.TokenData.DebitCardBrand
        ? capitalizeFirstLetterOfWords(transaction.TokenData.DebitCardBrand)
        : '',
      visible: !!transaction.TokenData.DebitCardBrand,
    },
    {
      label: t('AccountDetailsSection.Rows.debitCardExpiry', { defaultValue: 'Expiry Date' }),
      value:
        transaction.TokenData.DebitCardExpiryMonth && transaction.TokenData.DebitCardExpiryYear
          ? `${transaction.TokenData.DebitCardExpiryMonth}/${transaction.TokenData.DebitCardExpiryYear}`
          : '',
      visible: !!transaction.TokenData.DebitCardExpiryMonth && !!transaction.TokenData.DebitCardExpiryYear,
    },
  ];

  const hasAccountDetails = accountDetails.some(({ visible = true }) => visible);

  return (
    <Box flx="col" width="100%" gapSize="24px">
      {/* Legacy-only status — the new /scheduled-payment endpoint has no 'pending approval' state */}
      {transaction.Status === 'pending approval' && (
        <ApprovalPanel
          title={t('ApprovalPanel.title', { defaultValue: 'Status: Pending Approval' })}
          description={t('ApprovalPanel.description', {
            defaultValue: 'Approvers can find these transaction(s) in their Approval Inbox',
          })}
        />
      )}

      <ScheduledTransactionCard transaction={transaction} />

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('ScheduleDetailsSection.title', { defaultValue: 'Schedule Details' })}
        </Title>
        <DetailsRows details={scheduleDetails} />
      </Card>

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('AccountHolderDetailsSection.title', { defaultValue: 'Account Holder Details' })}
        </Title>
        <DetailsRows details={accountHolderDetails} />
      </Card>

      {hasAccountDetails && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {accountDetailsTitle}
          </Title>
          <DetailsRows details={accountDetails} />
        </Card>
      )}
    </Box>
  );
};

export default ScheduledTransactionOverview;
